import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { getUrlParamsFetch } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  Agenda,
  AgendaWithProspecto,
  AgendasPagingRes,
  AproveAgendaPylParamas,
  AproveRejectAgendaPyl,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

///* tanSctack query
export const usefetchAgendas = (params?: GetAgendasParams) => {
  return useQuery({
    queryKey: ['agendas', ...Object.values(params || {})],
    queryFn: async () => {
      return getAgendas(params);
    },
  });
};

export const useGetAgenda = (id: string) => {
  return useQuery({
    queryKey: ['agendas', id],
    queryFn: async () => {
      return getAgenda(id);
    },
  });
};

export const useGetAgendasPendingPago = (params?: GetAgendasParams) => {
  return useQuery({
    queryKey: ['agendas', ...Object.values(params || {})],
    queryFn: async () => {
      return getAgendasPendingPago(params);
    },
  });
};

export const useCreateAgenda = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAgenda,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agendas'] });

      navigate(returnUrl, { replace: true });
    },
  });
};

export const useConfirmAgendaPyL = (
  navigate: NavigateFunction,
  returnUrl: string,
  enableRedirect = true,
  enableAlert = true
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: aproveAgendaByPyL,
    onSuccess: res => {
      const setAlert = useUiStore.getState().setAlert;
      queryClient.invalidateQueries({ queryKey: ['agendas'] });

      if (res.status === 'success') {
        enableRedirect && navigate(returnUrl, { replace: true });
        enableAlert &&
          setAlert(true, 'Agenda aprobada correctamente', res.status);
        return;
      }
      enableRedirect && navigate(returnUrl, { replace: true });
    },
  });
};

export const useRejectAgendaPyL = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAgenda,
    onSuccess: res => {
      const setAlert = useUiStore.getState().setAlert;
      queryClient.invalidateQueries({ queryKey: ['agendas'] });

      if (res.status === 'success') {
        setAlert(true, 'Agenda rechazada correctamente', res.status);
      }
      return navigate(returnUrl, { replace: true });
    },
  });
};

///* axios
export type GetAgendasParams = {
  page?: number;
  page_size?: number;
  numero_contrato?: string; // search
  identificacion?: string; // search
  estado_pil?: number; // filter

  fecha_after?: string | null;
  fecha_before?: string | null;

  estado_pago?: string;
  pasa_revision?: boolean;
};

export type ValidateDataPylParams = Omit<
  AgendaWithProspecto,
  | 'id_agenda'
  // override
  | 'telefono_fijo'
  | 'telefono_adicional'
  | 'telefono_fijo_referencia'
  | 'numero_contrato'
  | 'url_contrato'
  | 'motivo'
  | 'estado_pil'
  | 'bloquear_hasta'
  | 'cliente'

  // fk
  | 'user'
> & {
  ///* override
  telefono_fijo?: string;
  telefono_adicional?: string;
  telefono_fijo_referencia?: string;
  numero_contrato?: string;
  url_contrato?: string;
  motivo?: string | null;
  user?: number | null;
  estado_pil?: number | null; // en ok pyl lo gestiono yo
  bloquear_hasta?: string | null;
  cliente?: string;
};

export type UpdateAgendaParams = {
  id: string;
  data: ValidateDataPylParams;
};

export const getAgendas = async (params?: GetAgendasParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<AgendasPagingRes>(
    `${urlBackend}api/v1/agendas/?${queryParams}`,
    true
  );
};

export const getAgenda = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<AgendaWithProspecto>(
    `${urlBackend}api/v1/agendas/${id}/`,
    true
  );

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createAgenda = async (data: ValidateDataPylParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Agenda>(`${urlBackend}api/v1/agendas/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateAgenda = async (params: UpdateAgendaParams) => {
  const res = await put<Agenda>(
    `${urlBackend}api/v1/agendas/${params.id}/`,
    params.data,
    true
  );

  return res;
};

export const aproveAgendaByPyL = async (params: AproveAgendaPylParamas) => {
  return put<AproveRejectAgendaPyl>(
    `${urlBackend}api/v1/agendas/`,
    {
      id: params.id,
      estado_pil: params.estado_pil,
    },
    true
  );
};

export const getAgendasPendingPago = async (params?: GetAgendasParams) => {
  const queryParams = getUrlParamsFetch(params || {});

  return get<AgendasPagingRes>(
    `${urlBackend}api/v1/agendas-instalacion-pago/?${queryParams}`,
    true
  );
};
